// this file the same as ReadOnlyGenericTask.tsx without the lookup hook
import { memo, Suspense, useState, useMemo, useEffect } from 'react';
import { useStore } from 'zustand';
import { Button, ScaleLogoLoading } from '@scale/scaleui';
import { useTranslateTask } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { normalize } from 'react-scale/corp/components/Lookup/normalize';
import { LookupTaskPreview } from 'react-scale/corp/components/Lookup/LookupTaskPreview';
import { useAppStore } from 'react-scale/corp/views/CorpProjectGroupManager/stores/AppStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ChatStoreContextProvider,
  useCreateChatStore,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore';
import { ReviewLevel, TaskType } from '@scale/shared-types';
import { ChatInteraction } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatInteraction';
import { Switch, Flex, Text, Select } from '@scale/scaleui-radix';
import { useShowContributorInstructionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useShowContributorInstructionsStore';
import { type UnresolvedPipelineV3NodeId } from '@scale/task-builder-shared';
import { useCommonChatTaskContextConfigs } from 'react-scale/components/scalers/tasks/Chat/hooks/configs';
import transformLookupToAssignment from '@scale/shared/assignmentJson/transformLookupToAssignment';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import useSupplementedMultiTaskResponseAssignment from 'react-scale/hooks/useSupplementedMultiTaskResponseAssignment';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'react-toastify';
import { ChatTaskWrapper } from './ChatTaskWrapper';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { getLogger } from '@scale/frontend-lib/datadog';
import {
  tryToStringify,
  isValidMongoId,
} from 'react-scale/corp/views/CorpProjectGroupManager/utils/helpers';
import { TaskParamsVersion } from './types';
import { useAsync } from 'react-use-v13';
import { loadResponseWithMaybeUrl } from 'react-scale/components/scalers/tasks/Chat/utils';

const logger = getLogger();

type ReadOnlyGenericTaskWithResponseProps = {
  attemptForTask: any;
  isLoadingAttemptedForTask: boolean;
  headerClassName?: string;
  extras?: {
    shouldShowContributorActions?: boolean;
    transactionHistoryId?: UnresolvedPipelineV3NodeId;
  };
  setCustomerView: (view: boolean) => void;
  customerView: boolean;
  overrideReviewLevel?: number;
  setOverrideReviewLevel?: (reviewLevel: number | undefined) => void;
  taskParamsVersion: TaskParamsVersion;
  setTaskParamsVersion: (version: TaskParamsVersion) => void;
};

export const ReadOnlyGenericTaskWithResponse = memo(
  ({
    attemptForTask,
    isLoadingAttemptedForTask,
    extras = {},
    headerClassName = '',
    setCustomerView,
    customerView,
    overrideReviewLevel,
    setOverrideReviewLevel,
    taskParamsVersion,
    setTaskParamsVersion,
  }: ReadOnlyGenericTaskWithResponseProps) => {
    const normalizedAttemptForTask = useMemo(
      () => (attemptForTask ? normalize(attemptForTask) : undefined),
      [attemptForTask],
    );
    const unifiedTaskViewRollout = useUnleashFeatureFlag('unified-task-view-rollout', {}, false);
    const [showTranslatedTask, setShowTranslatedTask] = useState(false);

    const {
      data: translatedTask,
      isLoading: isLoadingTranslatingTask,
      refetch: fetchTranslatedTask,
    } = useTranslateTask(attemptForTask);

    const normalizedTranslatedTask = useMemo(
      () => (translatedTask?.task ? normalize(translatedTask.task) : undefined),
      [translatedTask],
    );

    const handleTranslateClick = () => {
      if (!translatedTask?.task) {
        fetchTranslatedTask();
      }
      setShowTranslatedTask(!showTranslatedTask);
    };

    const handleErrorBoundaryError = (error: any, errorInfo: any) => {
      logger.error('Error rendering task', {
        taskOrAttemptId: attemptForTask?._id,
        errorInfo: tryToStringify(errorInfo, '-'),
        actualError: tryToStringify(error, '-'),
      });
      toast.error('Rendering the task failed. Please contact support if the error persists.');
    };

    const taskForNewPreview =
      showTranslatedTask && normalizedTranslatedTask
        ? normalizedTranslatedTask
        : normalizedAttemptForTask;

    const taskForOldPreview =
      showTranslatedTask && translatedTask?.task ? translatedTask?.task : normalizedAttemptForTask;

    const getTranslateButtonText = () => {
      if (showTranslatedTask) {
        return isLoadingTranslatingTask
          ? 'Translating...'
          : `View Original Task ${
              attemptForTask?.languageCode ? ' (' + attemptForTask.languageCode + ')' : ''
            }`;
      }
      return 'Translate Task Responses to English';
    };

    return (
      <div className="flex flex-col gap-2">
        <div className={twMerge('flex justify-end w-full items-center gap-2', headerClassName)}>
          {!customerView && (
            <>
              <Text size="2" as="label" htmlFor="task-params-version-select">
                Task Params Version:
              </Text>
              <Select.Root
                value={taskParamsVersion}
                onValueChange={value => {
                  setTaskParamsVersion(value as TaskParamsVersion);
                }}
              >
                <Select.Trigger className="min-w-[120px]" />
                <Select.Content>
                  <Select.Item value="most-recent">Most Recent</Select.Item>
                  <Select.Item value="time-of-tasking">At time of tasking</Select.Item>
                </Select.Content>
              </Select.Root>
            </>
          )}
          {!customerView && (
            <>
              <Text size="2" as="label" htmlFor="overrideReviewLevel-select">
                Override Review Level:
              </Text>
              <Select.Root
                value={
                  overrideReviewLevel === undefined ? 'undefined' : String(overrideReviewLevel)
                }
                onValueChange={val => {
                  setOverrideReviewLevel?.(val === 'undefined' ? undefined : Number(val));
                }}
              >
                <Select.Trigger className="min-w-[120px]" />
                <Select.Content>
                  <Select.Item value="undefined">Default</Select.Item>
                  <Select.Item value="-1">-1</Select.Item>
                  <Select.Item value="0">0</Select.Item>
                  <Select.Item value="1">1</Select.Item>
                  <Select.Item value="4">4</Select.Item>
                  <Select.Item value="8">8</Select.Item>
                  <Select.Item value="10">10</Select.Item>
                  <Select.Item value="11">11</Select.Item>
                  <Select.Item value="12">12</Select.Item>
                </Select.Content>
              </Select.Root>
            </>
          )}
          {!customerView && (
            <Button
              disabled={(showTranslatedTask && isLoadingTranslatingTask) || !attemptForTask}
              className="w-fit mt-1 ml-1"
              variant="outline"
              onClick={handleTranslateClick}
            >
              <FontAwesomeIcon
                icon={[
                  'fas',
                  showTranslatedTask && isLoadingTranslatingTask
                    ? 'spinner'
                    : showTranslatedTask
                    ? 'globe'
                    : 'flag-usa',
                ]}
                spin={showTranslatedTask && isLoadingTranslatingTask}
                className="mr-1"
              />
              {getTranslateButtonText()}
            </Button>
          )}
          <Text className="rt-reset" as="label" size="2" color="neutral">
            <Flex className="rt-reset" gap="2">
              <Switch
                className="rt-reset"
                disabled={!attemptForTask}
                color="accent"
                checked={customerView}
                onCheckedChange={setCustomerView}
              />
              View as Customer
            </Flex>
          </Text>
        </div>
        <ErrorBoundary
          key={`${overrideReviewLevel}-${taskParamsVersion}`}
          // TODO add generic error component
          fallback={<>Rendering the task failed. Please contact support if the error persists.</>}
          onError={handleErrorBoundaryError}
        >
          <div className="flex flex-col gap-2">
            {isLoadingAttemptedForTask ? (
              <div className="flex items-center justify-center h-full w-full pt-16">
                <ScaleLogoLoading size="lg" />
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-[40vh]">
                    <ScaleLogoLoading className="w-[250px]" />
                  </div>
                }
              >
                {unifiedTaskViewRollout ? (
                  <TaskPreviewNew
                    asCustomer={customerView}
                    task={taskForNewPreview}
                    extras={extras}
                    overrideReviewLevel={overrideReviewLevel}
                    taskParamsVersion={taskParamsVersion}
                    showTranslatedTask={showTranslatedTask}
                  />
                ) : (
                  <TaskPreviewOld
                    asCustomer={customerView}
                    task={taskForOldPreview}
                    extras={extras}
                  />
                )}
              </Suspense>
            )}
          </div>
        </ErrorBoundary>
      </div>
    );
  },
);

const TaskPreviewNew = memo(
  ({
    task,
    asCustomer,
    extras = {},
    overrideReviewLevel,
    taskParamsVersion,
    showTranslatedTask,
  }: {
    task: any;
    asCustomer: boolean;
    extras?: {
      shouldShowContributorActions?: boolean;
      transactionHistoryId?: UnresolvedPipelineV3NodeId;
    };
    overrideReviewLevel?: number;
    taskParamsVersion?: TaskParamsVersion;
    showTranslatedTask?: boolean;
  }) => {
    const config = useAppStore(s => s.config);

    return [TaskType.Chat, TaskType.ChatV2, TaskType.ChatExperimental].includes(
      task.type ?? task.taskType,
    ) ? (
      <ChatTaskWrapper
        key={`${task._id || task.id}-${taskParamsVersion}-${
          showTranslatedTask ? 'translated' : 'original'
        }`}
        task={task}
        asCustomer={asCustomer}
        extras={extras}
        overrideReviewLevel={overrideReviewLevel}
        taskParamsVersion={taskParamsVersion}
      />
    ) : (
      // If it's not a chat task, render the old lookup preview (this is the fallback in case someone opens a non-genai task such as robotics/video/gis/etc)
      <div className="my-4">
        <LookupTaskPreview
          overrideHeight={'60vh'}
          object={task}
          config={config}
          additionalText=""
          extras={extras}
        />
      </div>
    );
  },
);

// To keep previous behavior under the FF just in case we need to revert quickly
// This and all the below code should be removed in 1 or 2 weeks as ChatTaskWrapper encapsulates the new behavior
const TaskPreviewOld = memo(
  ({ task, asCustomer, extras }: { task: any; asCustomer: boolean; extras: any }) => {
    const config = useAppStore(s => s.config);
    return (task.type ?? task.taskType) === TaskType.Chat && asCustomer ? (
      <ChatV1Preview task={task} asCustomer={asCustomer} />
    ) : (
      <div className="my-4">
        <LookupTaskPreview
          overrideHeight={'60vh'}
          object={task}
          config={config}
          additionalText=""
          extras={extras}
        />
      </div>
    );
  },
);

const ChatV1Preview = memo(({ task, asCustomer = false }: { task: any; asCustomer?: boolean }) => {
  const config = task.taskParamsFromV3Node ?? task.params ?? task.task.params;
  const taskAssignment = transformLookupToAssignment(task);
  const supplementedAssignment = useSupplementedMultiTaskResponseAssignment(taskAssignment);
  const assignment = supplementedAssignment ?? taskAssignment;

  const chatStore = useCreateChatStore(
    {
      config,
      reviewLevel: assignment?.reviewLevel ?? ReviewLevel.Deliverable,
      hasActiveStepResponseLinterError: false,
      customerAuditShowInstructions: !asCustomer,
      isRealCustomerAudit: asCustomer,
    },
    undefined,
    'ChatV1Preview - ChatStore',
  );
  const setResponsesAndCompletedSteps = useStore(
    chatStore.current,
    state => state.setResponsesAndCompletedSteps,
  );

  const chatTaskFeatureConfig = assignment?.projectConfig?.chatTaskFeatureConfig;
  const commonChatTaskContextConfigs = useCommonChatTaskContextConfigs(
    config,
    assignment?.reviewLevel,
    chatTaskFeatureConfig,
    config.templateVariables,
  );
  const projectId = useMemo(
    () => [task.project._id, task.project, task.projectId].find(isValidMongoId),
    [task],
  );

  const { loading: isLoadingResponse } = useAsync(async () => {
    const response = await loadResponseWithMaybeUrl(task.response, projectId);
    setResponsesAndCompletedSteps(response.responses);
  });

  if (isLoadingResponse) {
    return (
      <div className="flex items-center justify-center h-full w-full pt-16">
        <ScaleLogoLoading size="lg" />
      </div>
    );
  }

  return (
    <div className="my-4">
      <ChatStoreContextProvider store={chatStore}>
        <ChatTaskContext.Provider
          value={{
            assignmentId: assignment?.assignmentId,
            taskAttemptIdWithFeedback: task.id,
            projectId,
            // workerId: props.loggedUserId,
            ...commonChatTaskContextConfigs,
            chatBenchmarkConfig: _.get(assignment, 'metadata.chatBenchmarkConfig'),
            // isInitialAttempt,
            isInitialAttempt: false,
            canEditComments: false,
            isPipelineV2: true,
            disableLinter: true, // props.isTaxonomyDisplay ? true : undefined,
            enableUnifiedFeedback: false, // shouldUseUnifiedFeedback || shouldUseAutoQCResults,
            contextSource: 'ChatBulkAudit',
          }}
        >
          <ChatV1WithProvider task={task} asCustomer={asCustomer} />
        </ChatTaskContext.Provider>
      </ChatStoreContextProvider>
    </div>
  );
});

const ChatV1WithProvider = memo(({ task, asCustomer }: { task: any; asCustomer: boolean }) => {
  const { setCustomerAuditShowInstructions } = useShowContributorInstructionsStore();
  useEffect(() => {
    setCustomerAuditShowInstructions(!asCustomer);
  }, [asCustomer, setCustomerAuditShowInstructions]);
  return <ChatInteraction key={task._id} readOnly enableLayoutOverride />;
});
